<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { condaApi } from '../condaApi';
import {
  msgs, input, last, session, newSessionId,
  reviewTurn, chanelMsgs, chanelInput, chanelThreadId, resetMotion,
} from '../state/motionState';

// The assistant's name from Personality, used in this page's copy. Falls back to
// "the bot" until settings load (or if no name is set).
const botName = ref('the bot');
onMounted(async () => {
  // Restored conversation (after navigating back): jump both chats to the latest.
  nextTick(() => {
    if (botScroll.value) botScroll.value.scrollTop = botScroll.value.scrollHeight;
    if (chanelScroll.value) chanelScroll.value.scrollTop = chanelScroll.value.scrollHeight;
  });
  try {
    const s = await condaApi.getSettings();
    if (s?.assistantName) botName.value = s.assistantName;
  } catch {
    // keep the fallback
  }
});

// Render bot/Chanel markdown (bold, lists, `code`) safely — the bot emits
// markdown the same as the live widget does; we just render it here too.
const mdIt = new MarkdownIt({ breaks: true, linkify: true });
const renderMd = t => DOMPurify.sanitize(mdIt.render(t || ''));

// Persistent conversation state (msgs, input, last, session, reviewTurn,
// chanelMsgs, chanelInput, proposal, chanelThreadId) lives in ../state/motionState
// so it survives navigating away and back. Transient UI flags stay local.
const busy = ref(false);
const showDetails = ref(false); // retrieval detail collapsed by default
const showPrompt = ref(false);
const chanelBusy = ref(false);
const botScroll = ref(null);      // live bot chat container
const chanelScroll = ref(null);   // Chanel chat container
const chanelBox = ref(null);      // Chanel input textarea, auto-grown to its content

// Retrieval shows only the start of an entry — make that visible.
const snip = (t, n = 160) => { t = t || ''; return t.length > n ? t.slice(0, n) + '…' : t; };

// Grow the Chanel box with what's typed (up to a few lines), shrink when cleared.
watch(chanelInput, () => nextTick(() => {
  const el = chanelBox.value; if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}));

// Keep each chat pinned to its latest message (or the typing dots).
const pin = el => nextTick(() => { if (el.value) el.value.scrollTop = el.value.scrollHeight; });
watch([() => msgs.value.length, busy], () => pin(botScroll));
watch([() => chanelMsgs.value.length, chanelBusy], () => pin(chanelScroll));
const reviewPrompt = () =>
  `In one or two sentences, tell me whether ${botName.value}'s last answer was good and the main reason — then ask what I'd like to do. Don't list recommendations.`;

async function send() {
  const text = input.value.trim();
  if (!text || busy.value) return;
  input.value = '';
  busy.value = true;
  const history = msgs.value.map(m => ({ role: m.role, content: m.content }));
  msgs.value.push({ role: 'user', content: text });
  try {
    const res = await condaApi.chat(text, history, session.value);
    last.value = res;
    msgs.value.push({ role: 'assistant', content: res.answer });
    reviewTurn.value = {
      message: text,
      answer: res.answer,
      via: res.via,
      confidence: res.confidence,
      context: (res.retrieved?.context || []).map(c => ({ ref: c.ref, source: c.source, content: c.content })),
      // The conversation before this turn, so Chanel reads the answer in context.
      history: history.map(h => ({ role: h.role === 'assistant' ? 'bot' : 'user', content: h.content })),
    };
    chanelMsgs.value = [];
    chanelThreadId.value = null;
    chanelAsk(reviewPrompt(), true); // auto first review (prompt hidden)
  } catch (e) {
    msgs.value.push({ role: 'assistant', content: `⚠ ${e.message}` });
  }
  busy.value = false;
}

// What goes back to the model: operator/chanel text turns, plus the OUTCOME of
// each proposal — Chanel doesn't apply changes itself, so without these notes it
// has no idea a change went live and keeps offering to draft it again.
function textHistory() {
  return chanelMsgs.value.flatMap(m => {
    if (m.role === 'operator' || m.role === 'chanel') return [{ role: m.role, content: m.content }];
    if (m.role === 'proposal' && m.status === 'applied')
      return [{ role: 'operator', content: `[applied] I clicked Apply — this change is now LIVE: ${m.summary}` }];
    if (m.role === 'proposal' && m.status === 'dismissed')
      return [{ role: 'operator', content: `[dismissed] I dismissed this proposed change (it was NOT applied): ${m.summary}` }];
    return [];
  });
}

async function chanelAsk(text, hidden = false) {
  if (!reviewTurn.value || chanelBusy.value) return;
  const history = textHistory();
  if (!hidden) chanelMsgs.value.push({ role: 'operator', content: text });
  chanelBusy.value = true;
  await nextTick();
  try {
    const r = await condaApi.chanel({ turn: reviewTurn.value, history, message: text, threadId: chanelThreadId.value, hidden });
    if (r.threadId) chanelThreadId.value = r.threadId;
    chanelMsgs.value.push({ role: 'chanel', content: r.reply });
    if (r.changeReady) await draftInline(); // show the concrete change inline, ready to apply
  } catch (e) {
    chanelMsgs.value.push({ role: 'chanel', content: `⚠ ${e.message}` });
  } finally {
    chanelBusy.value = false;
  }
}

// "clear" / "start over" / "…and clear" wipes the screen like the Clear button,
// instead of Chanel replying to it conversationally.
const isClearCmd = t =>
  /^\s*(clear|reset|wipe)(\s+(the\s+)?(chat|screen|this|everything|all|conversation))?\s*[.!]?\s*$/i.test(t) ||
  /^\s*(start (over|fresh|again)|new chat)\s*[.!]?\s*$/i.test(t) ||
  /\b(and|then|now)\s+clear\b\s*[.!]?\s*$/i.test(t);

function chanelSend() {
  const t = chanelInput.value.trim();
  if (!t) return;
  chanelInput.value = '';
  if (isClearCmd(t)) { clear(); return; }
  chanelAsk(t);
}

// Highlight just the changed span between two versions, with a little context.
function diffParts(before = '', after = '') {
  before = before || ''; after = after || '';
  let s = 0; const min = Math.min(before.length, after.length);
  while (s < min && before[s] === after[s]) s++;
  let eb = before.length, ea = after.length;
  while (eb > s && ea > s && before[eb - 1] === after[ea - 1]) { eb--; ea--; }
  const CTX = 90;
  const pre = after.slice(0, s), suf = after.slice(ea);
  return {
    prefix: pre.length > CTX ? '…' + pre.slice(-CTX) : pre,
    removed: before.slice(s, eb),
    added: after.slice(s, ea),
    suffix: suf.length > CTX ? suf.slice(0, CTX) + '…' : suf,
  };
}

// When Chanel signals a concrete change, draft it and drop it into the chat
// inline (highlighting only what changed), ready to Apply.
async function draftInline() {
  try {
    const p = await condaApi.propose({ turn: reviewTurn.value, history: textHistory() });
    if (!p.changes || !p.changes.length) return;
    const changes = p.changes.map(c => ({
      ...c,
      _diff: (c.type === 'edit_heart' || c.type === 'edit_entry') ? diffParts(c.before, c.after) : null,
    }));
    chanelMsgs.value.push({ role: 'proposal', summary: p.summary, changes, status: 'pending' });
  } catch (e) {
    chanelMsgs.value.push({ role: 'chanel', content: `⚠ ${e.message}` });
  }
}

// Apply happens ONLY here — Chanel never claims a change is done in prose.
async function applyInline(m) {
  if (m.status !== 'pending') return;
  m.status = 'applying';
  try {
    const clean = m.changes.map(({ _diff, ...c }) => c);
    const { results } = await condaApi.applyChanges(clean, chanelThreadId.value);
    const bad = results.filter(r => !r.ok);
    if (bad.length) { m.status = 'failed'; m.error = bad.map(b => `${b.target}: ${b.error}`).join('; '); return; }
    m.status = 'applied';
    // Change is live; start a fresh bot session so a re-ask reflects it.
    msgs.value = [];
    last.value = null;
    session.value = newSessionId();
    chanelMsgs.value.push({ role: 'chanel', content: `✓ Applied — I've started a fresh chat, ask ${botName.value} again to see it.` });
  } catch (e) {
    m.status = 'failed'; m.error = e.message;
  }
}

function clear() {
  resetMotion();
}

const changeLabel = c =>
  c.type === 'edit_heart' ? 'Edit Personality'
    : c.type === 'edit_entry' ? `Edit ${c.slug}.${c.field}`
    : c.type === 'add_entry' ? `Add ${c.slug}`
    : c.type === 'merge_entries' ? `Merge → ${c.into}` : c.type;
const scoreLabel = m => (m.strategy === 'semantic' ? `sim ${m.similarity?.toFixed(3)}` : `${m.score} ${m.strategy}`);
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <div class="row">
        <h1>Test Drive</h1>
        <span class="muted" style="font-size:12px">Chat with {{ botName }} live. Chanel reviews each answer.</span>
      </div>
      <button class="btn" @click="clear">Clear</button>
    </div>
    <div class="split">
      <!-- Left: live bot chat -->
      <div class="card" style="display:flex;flex-direction:column;min-height:0;overflow:hidden">
        <div ref="botScroll" class="chat" style="flex:1 1 auto;min-height:0;overflow-y:auto">
          <div v-if="!msgs.length" class="empty">Chat with {{ botName }}. Chanel reviews each answer on the right.</div>
          <div v-for="(m, i) in msgs" :key="i" class="bubble" :class="m.role === 'user' ? 'user' : 'bot'">
            <span v-if="m.role === 'user'">{{ m.content }}</span>
            <div v-else class="md" v-html="renderMd(m.content)" />
          </div>
          <div v-if="busy" class="bubble bot muted">…</div>
        </div>
        <div class="row" style="margin-top:12px;flex:0 0 auto">
          <input v-model="input" class="in" :placeholder="`Ask ${botName} something…`" @keydown.enter="send" />
          <button class="btn primary" :disabled="busy" @click="send">Send</button>
        </div>
      </div>

      <!-- Right: Chanel -->
      <div style="display:flex;flex-direction:column;gap:16px;min-height:0;height:100%">
        <div v-if="!reviewTurn" class="empty">Ask {{ botName }} something — Chanel reviews the answer and you can chat about improving it.</div>

        <template v-else>
          <div class="card" style="flex:1 1 0%;display:flex;flex-direction:column;min-height:0">
            <div class="row between"><strong>✨ Chanel</strong></div>
            <div ref="chanelScroll" class="chat" style="flex:1 1 auto;min-height:0;overflow-y:auto;margin-top:10px">
              <template v-for="(m, i) in chanelMsgs" :key="i">
                <div v-if="m.role === 'operator'" class="bubble user">{{ m.content }}</div>
                <div v-else-if="m.role === 'chanel'" class="bubble bot"><div class="md" v-html="renderMd(m.content)" /></div>
                <div v-else-if="m.role === 'proposal'" class="bubble bot proposal">
                  <div class="muted" style="font-size:12px;margin-bottom:8px">{{ m.summary }}</div>
                  <div v-for="(c, ci) in m.changes" :key="ci" style="margin-bottom:8px">
                    <span class="tag amber">{{ changeLabel(c) }}</span>
                    <div v-if="c._diff" class="diff mono"><span class="diff-ctx">{{ c._diff.prefix }}</span><span v-if="c._diff.removed" class="diff-del">{{ c._diff.removed }}</span><span class="diff-add">{{ c._diff.added }}</span><span class="diff-ctx">{{ c._diff.suffix }}</span></div>
                    <div v-else-if="c.type === 'add_entry'" class="diff mono">
                      <div><strong>{{ c.slug }}</strong> <span class="muted">{{ c.category }}</span></div>
                      <div class="muted" style="margin:2px 0 6px">{{ c.description }}</div>
                      <span class="diff-add">{{ c.response }}</span>
                    </div>
                    <div v-else-if="c.type === 'merge_entries'" class="diff mono">
                      Keep <strong>{{ c.into }}</strong>, remove {{ c.slugs.filter(s => s !== c.into).join(', ') }}
                      <div style="margin-top:6px"><span class="diff-add">{{ c.response }}</span></div>
                    </div>
                  </div>
                  <div v-if="m.status === 'pending'" class="row" style="gap:8px;margin-top:6px">
                    <button class="btn primary" @click="applyInline(m)">Apply this change</button>
                    <button class="btn" @click="m.status = 'dismissed'">Dismiss</button>
                  </div>
                  <div v-else-if="m.status === 'applying'" class="muted" style="margin-top:6px;font-size:12px">Applying…</div>
                  <div v-else-if="m.status === 'applied'" style="margin-top:6px"><span class="tag green">✓ Applied</span></div>
                  <div v-else-if="m.status === 'dismissed'" class="muted" style="margin-top:6px;font-size:12px">Dismissed</div>
                  <div v-else-if="m.status === 'failed'" class="muted" style="margin-top:6px;font-size:12px;color:#f6a6a6">⚠ {{ m.error }}</div>
                </div>
              </template>
              <div v-if="chanelBusy" class="bubble bot muted">…</div>
            </div>
            <div class="row" style="margin-top:12px;align-items:flex-end">
              <textarea
                ref="chanelBox"
                v-model="chanelInput"
                class="in chanel-in"
                rows="1"
                placeholder="Ask Chanel… (e.g. make it answer general questions)"
                @keydown.enter.exact.prevent="chanelSend"
              />
              <button class="btn primary" :disabled="chanelBusy" @click="chanelSend">Send</button>
            </div>
          </div>

          <!-- Retrieval details (collapsed) — only for live turns -->
          <div v-if="last" class="card" style="flex:0 0 auto">
            <div class="row between" style="cursor:pointer" @click="showDetails = !showDetails">
              <strong>Retrieval details</strong>
              <span class="muted"><span class="pill">{{ last.via }}</span> {{ showDetails ? 'hide' : 'show' }}</span>
            </div>
            <div v-if="showDetails" style="margin-top:12px;max-height:40vh;overflow:auto">
              <div class="row between">
                <span>Path: <span class="pill">{{ last.via }}</span></span>
                <span class="row">
                  <span v-if="last.shouldHandover" class="tag amber">handover</span>
                  <span class="tag" :class="last.usedLlm ? 'green' : ''">{{ last.usedLlm ? 'LLM' : 'KB-verbatim' }}</span>
                </span>
              </div>
              <div style="margin-top:12px"><strong>Injected context ({{ last.retrieved.context.length }})</strong></div>
              <div class="list" style="margin-top:8px">
                <div v-for="(m, i) in last.retrieved.context" :key="i" class="item" style="cursor:default;padding:8px 10px">
                  <div class="row between"><span><span class="pill">{{ m.source }}</span> <strong>{{ m.ref }}</strong></span><span class="muted">{{ scoreLabel(m) }}</span></div>
                  <div class="muted mono" style="margin-top:4px">{{ snip(m.content) }}</div>
                </div>
              </div>
              <div style="margin-top:12px"><strong>Semantic matches ({{ last.retrieved.semantic.length }})</strong></div>
              <div class="list" style="margin-top:8px">
                <div v-for="(m, i) in last.retrieved.semantic" :key="i" class="item" style="cursor:default;padding:8px 10px">
                  <div class="row between"><span><span class="pill">{{ m.source }}</span> <strong>{{ m.ref }}</strong></span><span class="muted">{{ scoreLabel(m) }}</span></div>
                </div>
              </div>
              <div class="row between" style="cursor:pointer;margin-top:12px" @click="showPrompt = !showPrompt">
                <strong>Injected system prompt</strong><span class="muted">{{ showPrompt ? 'hide' : 'show' }}</span>
              </div>
              <div v-if="showPrompt" class="mono" style="margin-top:10px">{{ last.injectedSystemPrompt }}</div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>

<!-- Unscoped: scoped styles don't reach v-html content. Contained under .conda-page .md -->
<style>
.conda-page .md { white-space: normal; line-height: 1.5; }
.conda-page .md > :first-child { margin-top: 0; }
.conda-page .md > :last-child { margin-bottom: 0; }
.conda-page .md p { margin: 0 0 8px; }
.conda-page .md ul, .conda-page .md ol { margin: 6px 0; padding-left: 20px; }
.conda-page .md li { margin: 3px 0; }
.conda-page .md strong { font-weight: 700; }
.conda-page .md em { font-style: italic; }
.conda-page .md a { color: #a5b4fc; text-decoration: underline; }
.conda-page .md code { background: rgba(255,255,255,.10); padding: 1px 5px; border-radius: 5px; font-family: ui-monospace, Menlo, monospace; font-size: 12px; }
.conda-page .md pre { background: rgba(0,0,0,.30); padding: 10px; border-radius: 8px; overflow: auto; }
.conda-page .md pre code { background: none; padding: 0; }
.conda-page .md h1, .conda-page .md h2, .conda-page .md h3 { font-size: 14px; font-weight: 700; margin: 10px 0 4px; }
.conda-page .md blockquote { border-left: 3px solid #2a2f3a; margin: 6px 0; padding-left: 10px; color: #9aa1ad; }
</style>
